import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ApiServiceErr, axiosApi } from "./apiService";

interface country {
  key: string;
  text: string;
  value: string;
}

interface produceType {
  key: string;
  text: string;
  value: string;
}
export const useGetCountries = () => {
  return useQuery<country[], ApiServiceErr>(["getCountries"], async () => {
    const response = await axiosApi.get("/static-data/countries");
    return response.data;
  });
};

export const useGetCountriesWithAlpha = () => {
  return useQuery<country[], ApiServiceErr>(["getCountriesWithAlpha"], async () => {
    const response = await axiosApi.get("/static-data/countries");
    const countryData = response.data?.map((c: any) => ({
      key: c.id,
      text: c.country,
      code: c.alpha_code,
      value: c.id
    }));
    return countryData;
  });
};

export const useGetCountriesData = () => {
  return useQuery<country[], ApiServiceErr>(["getCountriesData"], async () => {
    const response = await axiosApi.get("/static-data/countries");
    return response.data.map((item: any) => {
      return {
        key: item.id,
        text: item.country,
        value: item.id
      };
    });
  });
};

export const useGetProduceTypes = () => {
  return useQuery<produceType[], ApiServiceErr>(["getProduceTypes"], async () => {
    const response = await axiosApi.get("/static-data/produce-types");
    return response.data;
  });
};

export const useGetTransportationTypes = () => {
  return useQuery<produceType[], ApiServiceErr>(["getTransportationTypes"], async () => {
    const response = await axiosApi.get("/static-data/transportation-types");
    return response.data;
  });
};

export const useGetChemicalTypes = () => {
  return useQuery<produceType[], ApiServiceErr>(["getChemicalTypes"], async () => {
    const response = await axiosApi.get("/static-data/chemical-list");
    return response.data.map((item: any) => {
      return {
        key: item.id,
        value: item.id,
        text: item.chemical_name
      };
    });
  });
};

export const useChemicalTypeMap = () => {
  const { data: chemicalTypes = [] } = useGetChemicalTypes();

  const chemicalTypeMap = useMemo(() => {
    const map: Record<number, string> = {};
    chemicalTypes.forEach((ct: any) => {
      map[Number(ct.value)] = ct.text;
    });
    return map;
  }, [chemicalTypes]);

  return chemicalTypeMap;
};

export const useGetFertilizerList = () => {
  return useQuery<produceType[], ApiServiceErr>(["getFertilizerList"], async () => {
    const response = await axiosApi.get("/static-data/fertilizer-list");
    return response.data;
  });
};

export const useGetPesticideList = () => {
  return useQuery<produceType[], ApiServiceErr>(["getPesticideList"], async () => {
    const response = await axiosApi.get("/static-data/pesticide-list");
    return response.data;
  });
};

export const useGetWasteManagementTypes = () => {
  return useQuery<produceType[], ApiServiceErr>(["getChemicalTypes"], async () => {
    const response = await axiosApi.get("/static-data/waste-management-types");
    return response.data.map((item: any) => {
      return {
        key: item.id,
        value: item.id,
        text: item.chemical_name
      };
    });
  });
};

export const useGetWasteManagementWasteTypes = () =>
  useQuery<any, ApiServiceErr>(
    ["getWasteMangementWasteData"],
    async () => {
      const response = await axiosApi.get(`/static-data/waste-management-types`);

      let wasteManagementTypes = response?.data.reduce((currentType: any, data: any) => {
        let wasteType = {
          key: data?.id,
          text: data?.waste_management_type,
          value: data?.id
        };
        return [...currentType, wasteType];
      }, []);
      return wasteManagementTypes;
    },
    {
      cacheTime: 0
    }
  );

export const useGetSpecifyIngredient = () =>
  useQuery<any, ApiServiceErr>(
    ["getWasteMangementWasteData"],
    async () => {
      const response = await axiosApi.get(`/static-data/process-produce-types`);
      let wasteManagementTypes = response?.data.reduce((currentType: any, data: any) => {
        let wasteType = {
          key: data?.id,
          text: data?.process_produce_name,
          value: data?.id
        };
        return [...currentType, wasteType];
      }, []);
      return wasteManagementTypes;
    },
    {
      cacheTime: 0
    }
  );

export const useGetIrrigationMethod = () =>
  useQuery<any, ApiServiceErr>(
    ["getIrrigationMethod"],
    async () => {
      const response = await axiosApi.get(`/static-data/irrigation-methods`);
      let irrigationData = response?.data.reduce((currentType: any, data: any) => {
        let irrigation = {
          key: data?.id,
          text: data?.irrigation_method_name,
          value: data?.id
        };
        return [...currentType, irrigation];
      }, []);
      return irrigationData;
    },
    {
      cacheTime: 0
    }
  );

export const useGetPowerSource = () =>
  useQuery<any, ApiServiceErr>(
    ["getPowerSource"],
    async () => {
      const response = await axiosApi.get(`/static-data/irrigation-power-sources`);
      let powerSource = response?.data.reduce((currentType: any, data: any) => {
        let powerSourceData = {
          key: data?.id,
          text: data?.irrigation_power_sources_name,
          value: data?.id
        };
        return [...currentType, powerSourceData];
      }, []);
      return powerSource;
    },
    {
      cacheTime: 0
    }
  );

export const useGetEnergySource = () =>
  useQuery<any, ApiServiceErr>(
    ["getEnergySource"],
    async () => {
      const response = await axiosApi.get(`/static-data/fuel-use-energy-sources`);
      let energySource = response?.data.reduce((currentType: any, data: any) => {
        let powerSourceData = {
          key: data?.id,
          text: data?.fuel_use_energy_source_name,
          value: data?.id
        };
        return [...currentType, powerSourceData];
      }, []);
      return energySource;
    },
    {
      cacheTime: 0
    }
  );

export const useGetLandUsage = () =>
  useQuery<any, ApiServiceErr>(
    ["getLandUsageChangeList"],
    async () => {
      const response = await axiosApi.get(`/static-data/land-use-change-list`);
      let landUsages = response?.data.reduce((currentType: any, data: any) => {
        let landUsage = {
          key: data?.id,
          text: data?.land_use_chanage_name,
          value: data?.land_use_chanage_name
        };
        return [...currentType, landUsage];
      }, []);
      return landUsages;
    },
    {
      cacheTime: 0
    }
  );

export const useGetHeatSourceType = () =>
  useQuery<any, ApiServiceErr>(
    ["getHeatSourceTypeData"],
    async () => {
      const response = await axiosApi.get(`/static-data/heat-sources`);
      let currentHeatType = response?.data.reduce((currentType: any, data: any) => {
        let heatType = {
          key: data?.id,
          text: data?.heat_type,
          value: data?.id
        };
        return [...currentType, heatType];
      }, []);
      return currentHeatType;
    },
    {
      cacheTime: 0
    }
  );

export const useGetWaterSourceType = () =>
  useQuery<any, ApiServiceErr>(
    ["getWaterSourceTypeData"],
    async () => {
      const response = await axiosApi.get(`/static-data/water-sources`);
      let currentWaterType = response?.data.reduce((currentType: any, data: any) => {
        let waterType = {
          key: data?.id,
          text: data?.water_type,
          value: data?.id
        };
        return [...currentType, waterType];
      }, []);
      return currentWaterType;
    },
    {
      cacheTime: 0
    }
  );

export const useGetRefrigerantSourceType = () =>
  useQuery<any, ApiServiceErr>(
    ["refrigerantSource"],
    async () => {
      const response = await axiosApi.get(`/static-data/refrigerant-sources`);
      let currentRefrigerantType = response?.data.reduce((currentType: any, data: any) => {
        let refrigerantSource = {
          key: data?.id,
          text: data?.refrigerant_type,
          value: data?.id
        };
        return [...currentType, refrigerantSource];
      }, []);
      return currentRefrigerantType;
    },
    {
      cacheTime: 0
    }
  );

export const useGetCipSipChemicalTypes = () => {
  return useQuery<any, ApiServiceErr>(["getCipsipChemicalTypes"], async () => {
    const response = await axiosApi.get("/static-data/cipsip-chemical-list");
    return response.data.map((item: any) => {
      return {
        key: item.id,
        value: item.id,
        text: item.chemical_name
      };
    });
  });
};

export const useGetPackagingMaterial = () =>
  useQuery<any, ApiServiceErr>(
    ["getPackagingType"],
    async () => {
      const response = await axiosApi.get(`/static-data/packaging-types`);
      let createPackagingType = response?.data.reduce((currentType: any, data: any) => {
        let packagingTypeData = {
          key: data?.id,
          text: data?.packaging_name,
          value: data?.id
        };
        return [...currentType, packagingTypeData];
      }, []);
      createPackagingType.sort((a: any, b: any) => a.text.localeCompare(b.text));
      return createPackagingType;
    },
    {
      cacheTime: 0
    }
  );

export const useGetCountryCurrency = () => {
  return useQuery<any[], ApiServiceErr>(["getCountryCurrency"], async () => {
    const response = await axiosApi.get("/static-data/country-currency");
    return response.data;
  });
};

//cultivation static data

export const useGetProduceType = () => {
  return useQuery<produceType[], ApiServiceErr>(["getProduceTypesMapped"], async () => {
    const response = await axiosApi.get("/static-data/produce-types");
    let produceType = response?.data?.map((p: any) => ({
      text: p.produce_name,
      key: p.id,
      value: p.id
    }));
    return produceType;
  });
};

export const useGetFertilizerData = () => {
  return useQuery<any[], ApiServiceErr>(["getFertilizerList"], async () => {
    const response = await axiosApi.get("/static-data/fertilizer-list");
    const fertilizerData = response?.data?.map((f: any) => ({
      key: f.id,
      text: f.fertilizer_name,
      value: f.id,
      ...f
    }));
    return fertilizerData || [];
  });
};

export const useGetPesticideData = () => {
  return useQuery<produceType[], ApiServiceErr>(["getPesticideList"], async () => {
    const response = await axiosApi.get("/static-data/pesticide-list");
    return response?.data?.map((p: any) => ({
      ...p,
      id: parseInt(p.id),
      key: p.id,
      text: p.pesticide_name,
      value: p.id
    }));
  });
};

export const useGetTransportTypesList = () =>
  useQuery(
    ["getTransportTypesList"],
    async () => {
      const response = await axiosApi.get(`/static-data/transportation-types`);

      const newD = await response?.data?.map((type: any) => {
        const newIngredient: any = {
          key: type?.id,
          text: type?.transportation_name,
          value: type?.id
        };
        return newIngredient;
      });
      return newD;
    },
    {
      refetchOnWindowFocus: false
    }
  );

export interface StaticRecipeIngredient {
  key: number;
  text: string;
  value: number;
  description: string | null;
  status: string | null;
  record: string | null;
  allocationMethod: string | null;
  databaseName: string | null;
}

export const useGetStaticRecipeIngredientData = () =>
  useQuery<StaticRecipeIngredient[], ApiServiceErr>(
    ["useGetStaticRecipeIngredientData"],
    async () => {
      const response = await axiosApi.get(`/static-data/recipe-ingredients`);
      const ingredientDetails = response?.data.reduce(
        (currentType: StaticRecipeIngredient[], data: any) => {
          const ingredientData: StaticRecipeIngredient = {
            key: data?.id,
            text: data?.name,
            value: data?.id,
            description: data?.description ?? null,
            status: data?.status ?? null,
            record: data?.record ?? null,
            allocationMethod: data?.allocation_method ?? null,
            databaseName: data?.database_name ?? null
          };
          return [...currentType, ingredientData];
        },
        []
      );
      return ingredientDetails;
    },
    {
      cacheTime: 0
    }
  );

export const useGetStaticRecipeProcessTypes = () =>
  useQuery<any, ApiServiceErr>(
    ["useGetStaticRecipeProcessType"],
    async () => {
      const response = await axiosApi.get(`/static-data/recipe-process-types`);
      let RecipeSpecificGenericProcess = response?.data.reduce((currentType: any, data: any) => {
        let genericProcess = {
          key: data?.id,
          text: data?.process_name,
          value: data?.id
        };
        return [...currentType, genericProcess];
      }, []);
      return RecipeSpecificGenericProcess;
    },
    {
      cacheTime: 0
    }
  );
